import { useRouter, type Href } from 'expo-router';
import { useCallback } from 'react';

import {
  browserLedgerStorage,
  currentHistoryEntryId,
  hasAppEntryBehind,
} from '@/util/historyLedger';
import { resolveBack } from '@/util/resolveBack';

/**
 * Goes back exactly as the browser's back button would, whenever the entry before this one is the
 * app's, and otherwise replaces the page with its parent.
 *
 * It asks the tab's history ledger rather than `canGoBack`, which reads an in-memory stack that a
 * reload rebuilds from the URL alone, and it calls `history.back()` rather than `router.back()`,
 * which after a reload has nothing to pop. The router rebuilds its state from the entry it lands
 * on, as it does for the browser's own button.
 */
export function useGoBack(backRoute?: Href) {
  const router = useRouter();

  return useCallback(() => {
    const action = resolveBack({
      hasAppEntryBehind: hasAppEntryBehind(browserLedgerStorage(), currentHistoryEntryId()),
      backRoute,
    });

    if (action.kind === 'history') {
      window.history.back();

      return;
    }

    router.replace(action.href);
  }, [router, backRoute]);
}
